import { useCallback, useEffect, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureFlag } from '@trello/feature-flag-client';

import { reasonControlGroup, reasonNoExperiment } from '../DisabledReasons';
import { getAvailableSites } from '../getAvailableSites';
import type { TouchpointSourceType } from '../TouchpointSourceType';
import type { TrelloCrossFlow } from '../TrelloCrossFlow';
import { useEligibleWorkspacesForProvisioning } from '../useEligibleWorkspacesForProvisioning';
import { isSourceTargetedByFeatureFlag } from './isSourceTargetedByFeatureFlag';

const CROSS_FLOW_FOR_FUNNEL_EXP = 'xf.cffe-for-funnel-experiment';

const oneTimeMessageSource: Partial<Record<TouchpointSourceType, string>> = {
  cardBackDatePicker: '1p-discovery-ad-card-date-range-picker',
};

export const useCrossFlowForFunnelExperiment = ({
  source,
  workspaceId,
}: {
  source: TouchpointSourceType;
  workspaceId?: string;
}): TrelloCrossFlow => {
  const crossFlow = useCrossFlow();

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const isSourceOneTimeMessageDismissed = useCallback(
    () => isOneTimeMessageDismissed(oneTimeMessageSource?.[source] || ''),
    [source, isOneTimeMessageDismissed],
  );

  const { options: freeWorkspaces } =
    useEligibleWorkspacesForProvisioning(workspaceId);

  const [sendExposureEvent, setSendExposureEvent] = useState(false);

  const [crossFlowExperiment, setCrossFlowExperiment] =
    useState<TrelloCrossFlow>({
      isEnabled: false,
      reason: reasonNoExperiment,
    });

  const crossFlowForFunnelExp = useFeatureFlag<
    'control' | 'not-enrolled' | 'experiment'
  >(CROSS_FLOW_FOR_FUNNEL_EXP, 'not-enrolled', {
    sendExposureEvent,
  });

  useEffect(() => {
    let loadingAvailableSites = false;
    const evaluateExperimentEligibility = async () => {
      try {
        loadingAvailableSites = true;
        const { sites } = await getAvailableSites();
        const hasNoSites =
          sites?.filter(({ cloudId }) => cloudId !== 'TRELLO').length === 0;
        const hasFreeWorkspaces = freeWorkspaces.length >= 1;
        if (
          !isSourceOneTimeMessageDismissed() &&
          hasNoSites &&
          hasFreeWorkspaces &&
          crossFlow.isEnabled
        ) {
          setSendExposureEvent(true);
          if (crossFlowForFunnelExp === 'experiment') {
            setCrossFlowExperiment({
              isEnabled: true,
              open: () =>
                crossFlow.api.open({
                  targetProduct: 'jira-core.ondemand',
                  journey: 'get-started',
                  sourceComponent: source,
                  sourceContext: 'get-started',
                  experimentalOptions: {
                    trelloWorkspaceId: workspaceId || null,
                  },
                }),
            });
          } else {
            setCrossFlowExperiment({
              isEnabled: false,
              reason: reasonControlGroup,
            });
          }
        }
      } finally {
        loadingAvailableSites = false;
      }
    };
    if (
      !loadingAvailableSites &&
      crossFlow.isEnabled &&
      crossFlowForFunnelExp !== 'not-enrolled' &&
      isSourceTargetedByFeatureFlag({
        flagName: CROSS_FLOW_FOR_FUNNEL_EXP,
        source,
      })
    ) {
      evaluateExperimentEligibility();
    }
    // Intentionally ignoring the crossFlow dependency
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [
    isSourceOneTimeMessageDismissed,
    crossFlowForFunnelExp,
    freeWorkspaces.length,
    source,
    workspaceId,
  ]);

  return crossFlowExperiment;
};
